/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FtoSearchDirectorService } from '../fto-search-director-service';
import { SearchRoundsService } from '../search-rounds-service';
import { PatentsService } from '../patents-service';
import type { FtoSimulationConfig, FtoExecutionResult } from '../fto-simulation-config';
import { DEFAULT_SIMULATION_CONFIG } from '../fto-simulation-config';
import type { SearchRound } from '../../types';
import { CurrentStep } from '../../enums';

/**
 * FTO 检索导演服务实现
 * 通过简化的 run 方法模拟完整的 FTO 检索流程
 */
@injectable()
export class FtoSearchDirectorServiceImpl implements FtoSearchDirectorService {
    private _isRunning = false;
    private _currentExecution: Promise<FtoExecutionResult[]> | null = null;
    private _abortController: AbortController | null = null;
    private _executionResults: FtoExecutionResult[] = [];

    constructor(
        @inject(SearchRoundsService)
        private readonly _searchRoundsService: SearchRoundsService,
        @inject(PatentsService)
        private readonly _patentsService: PatentsService,
    ) {}

    async run(config: FtoSimulationConfig): Promise<FtoExecutionResult[]> {
        if (this._isRunning) {
            throw new Error('已有模拟正在运行中，请先停止当前模拟');
        }

        console.log('🚀 开始运行 FTO 检索模拟');

        // 合并默认配置
        const mergedConfig = this._mergeWithDefaults(config);

        // 重置状态
        this._isRunning = true;
        this._abortController = new AbortController();
        this._executionResults = [];

        try {
            // 清空现有数据（如果需要）
            if (mergedConfig.clearBeforeStart) {
                this._patentsService.clearPatents();
            }

            // 初始化搜索轮次
            const initialRounds = this._createInitialRounds(mergedConfig);
            this._searchRoundsService.setSearchRounds(initialRounds);

            // 执行模拟
            this._currentExecution = this._executeSimulation(mergedConfig);
            const results = await this._currentExecution;

            console.log('✅ FTO 检索模拟完成');
            return results;

        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('⏹️ FTO 检索模拟被用户停止');
                throw new Error('模拟被用户停止');
            }

            console.error('❌ FTO 检索模拟发生错误:', error);

            if (mergedConfig.callbacks?.onError) {
                mergedConfig.callbacks.onError(error as Error, -1);
            }

            throw error;
        } finally {
            this._isRunning = false;
            this._currentExecution = null;
            this._abortController = null;
        }
    }

    async stop(): Promise<void> {
        if (!this._isRunning || !this._abortController) {
            return;
        }

        console.log('🛑 停止 FTO 检索模拟');
        this._abortController.abort();

        if (this._currentExecution) {
            try {
                await this._currentExecution;
            } catch {
                // 忽略停止时的错误
            }
        }
    }

    isRunning(): boolean {
        return this._isRunning;
    }

    getRunningStatus() {
        const allRounds = this._searchRoundsService.getAllSearchRounds();

        return {
            isRunning: this._isRunning,
            totalRounds: allRounds.length,
            completedRounds: allRounds.filter(r => r.status === 'completed').length,
            runningRounds: allRounds.filter(r => r.status === 'running').length,
            failedRounds: allRounds.filter(r => r.status === 'error').length,
        };
    }

    // 私有方法：合并默认配置
    private _mergeWithDefaults(config: FtoSimulationConfig): Required<FtoSimulationConfig> {
        return {
            ...DEFAULT_SIMULATION_CONFIG,
            ...config,
            globalStepDelays: {
                ...DEFAULT_SIMULATION_CONFIG.globalStepDelays,
                ...config.globalStepDelays,
            },
            callbacks: {
                ...config.callbacks,
            },
        } as Required<FtoSimulationConfig>;
    }

    // 私有方法：创建初始搜索轮次
    private _createInitialRounds(config: Required<FtoSimulationConfig>): SearchRound[] {
        return config.searchRounds.map((roundConfig, index) => ({
            query: roundConfig.query,
            isManual: roundConfig.isManual ?? false,
            patentCount: 0,
            status: 'pending' as const,
            currentStep: CurrentStep.Query,
            isSelected: index === 0, // 默认选中第一个
        }));
    }

    // 私有方法：执行模拟
    private async _executeSimulation(config: Required<FtoSimulationConfig>): Promise<FtoExecutionResult[]> {
        const { searchRounds, executionMode } = config;

        if (executionMode === 'parallel') {
            // 并行执行所有轮次
            const promises = searchRounds.map((roundConfig, index) =>
                this._executeSearchRound(index, roundConfig, config),
            );
            return Promise.all(promises);
        } else {
            // 串行执行
            const results: FtoExecutionResult[] = [];
            for (let index = 0; index < searchRounds.length; index++) {
                const result = await this._executeSearchRound(index, searchRounds[index], config);
                results.push(result);

                // 检查是否被中止
                this._checkAbortSignal();
            }
            return results;
        }
    }

    // 私有方法：执行单个搜索轮次
    private async _executeSearchRound(
        roundIndex: number,
        roundConfig: Required<FtoSimulationConfig>['searchRounds'][0],
        globalConfig: Required<FtoSimulationConfig>,
    ): Promise<FtoExecutionResult> {
        const startTime = Date.now();
        const result: FtoExecutionResult = {
            roundIndex,
            query: roundConfig.query,
            success: false,
            finalStep: -1,
            finalPatentCount: 0,
            executionTime: 0,
            stepDetails: [],
        };

        try {
            // 触发开始回调
            globalConfig.callbacks?.onRoundStart?.(roundIndex, roundConfig.query);

            // 更新状态为运行中
            this._searchRoundsService.updateSearchRoundStatus(roundIndex, 'running');

            // 执行步骤序列
            const targetStep = roundConfig.targetStep ?? 3;
            const steps = this._getStepsToExecute(targetStep);

            for (const step of steps) {
                this._checkAbortSignal();

                const stepStartTime = Date.now();

                // 更新当前步骤
                this._searchRoundsService.updateCurrentStep(roundIndex, step);

                // 获取延迟时间
                const delay = roundConfig.customStepDelays?.[step] ??
                             globalConfig.globalStepDelays[step] ??
                             2000;

                // 模拟执行时间
                await this._delay(delay);

                // 加载专利数据
                await this._patentsService.loadPatentsByRoundAndStep(roundIndex, step);

                // 更新专利数量
                const patentCount = this._patentsService.getPatentsCount();
                this._searchRoundsService.updatePatentCount(roundIndex, patentCount);

                const stepDuration = Date.now() - stepStartTime;

                // 记录步骤详情
                result.stepDetails.push({
                    step,
                    patentCount,
                    duration: stepDuration,
                    success: true,
                });

                // 触发步骤完成回调
                globalConfig.callbacks?.onStepComplete?.(roundIndex, step, patentCount);

                console.log(`🔍 轮次${roundIndex + 1} - ${this._getStepName(step)} 完成，找到 ${patentCount} 个专利`);
            }

            // 成功完成
            result.success = true;
            result.finalStep = targetStep;
            result.finalPatentCount = this._patentsService.getPatentsCount();
            this._searchRoundsService.updateSearchRoundStatus(roundIndex, 'completed');

        } catch (error) {
            result.success = false;
            result.error = error instanceof Error ? error.message : String(error);
            this._searchRoundsService.updateSearchRoundStatus(roundIndex, 'error');

            // 根据错误处理策略决定是否继续
            if (globalConfig.onError === 'stop') {
                throw error;
            }
        } finally {
            result.executionTime = Date.now() - startTime;

            // 更新进度
            globalConfig.callbacks?.onProgress?.(this.getRunningStatus());

            // 触发轮次完成回调
            globalConfig.callbacks?.onRoundComplete?.(
                roundIndex,
                roundConfig.query,
                result.success,
            );
        }

        return result;
    }

    // 私有方法：获取需要执行的步骤
    private _getStepsToExecute(targetStep: CurrentStep): Array<CurrentStep> {
        const allSteps: Array<CurrentStep> = [CurrentStep.Query, CurrentStep.FilterStep1, CurrentStep.FilterStep2, CurrentStep.FilterStep3, CurrentStep.Final];
        return allSteps.filter(step => step <= targetStep);
    }

    // 私有方法：延迟函数
    private async _delay(ms: number): Promise<void> {
        return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(resolve, ms);

            this._abortController?.signal.addEventListener('abort', () => {
                clearTimeout(timeoutId);
                reject(new Error('AbortError'));
            });
        });
    }

    // 私有方法：检查中止信号
    private _checkAbortSignal(): void {
        if (this._abortController?.signal.aborted) {
            throw new Error('AbortError');
        }
    }

    // 私有方法：获取步骤名称
    private _getStepName(step: CurrentStep): string {
        const stepNames: Record<CurrentStep, string> = {
            [CurrentStep.Query]: '查询步骤',
            [CurrentStep.FilterStep1]: '筛选步骤1 (R1)',
            [CurrentStep.FilterStep2]: '筛选步骤2 (R2)',
            [CurrentStep.FilterStep3]: '筛选步骤3 (R3)',
            [CurrentStep.Final]: '最终结果',
        };
        return stepNames[step] || `步骤${step}`;
    }

    // 私有方法：获取步骤键值
}
